import React, { useState, useEffect } from 'react';
import { Accident, UserSettings, WazeDataState } from '../types';
import { AccidentCard } from './AccidentCard';
import { UserBlockModal } from './UserBlockModal';
import { RefreshCw, Wifi, WifiOff, AlertCircle } from 'react-feather';
import './ListView.css';

interface ListViewProps {
  accidents: Accident[];
  onViewOnMap: (accident: Accident) => void;
  onReplacementRequest: (accident: Accident) => void;
  userSettings: UserSettings;
  onSettingsChange: (settings: UserSettings) => void;
  wazeDataState?: WazeDataState;
  onRefresh?: () => void;
}

export const ListView: React.FC<ListViewProps> = ({ 
  accidents, 
  onViewOnMap, 
  onReplacementRequest,
  userSettings, 
  onSettingsChange,
  wazeDataState,
  onRefresh
}) => {
  const [filterBy, setFilterBy] = useState<'filtered' | 'all'>('filtered');
  const [filteredAccidents, setFilteredAccidents] = useState<Accident[]>(accidents);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  useEffect(() => {
    let filtered = accidents;
    
    // Apply user blocking filter
    if (filterBy === 'filtered') {
      filtered = accidents.filter(accident => !userSettings.blockedUsers.includes(accident.submittedBy));
    }
    
    setFilteredAccidents(filtered);
  }, [accidents, filterBy, userSettings.blockedUsers]);

  const handleUserClick = (username: string) => {
    setSelectedUser(username);
  };

  const handleBlockUser = (username: string) => {
    if (!userSettings.blockedUsers.includes(username)) {
      onSettingsChange({
        ...userSettings,
        blockedUsers: [...userSettings.blockedUsers, username]
      });
    }
    setSelectedUser(null);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  // Helper function to check if accident is within last 10 minutes
  const isRecentAccident = (accident: Accident): boolean => {
    const now = new Date();
    const tenMinutesAgo = now.getTime() - (10 * 60 * 1000);
    return accident.timestamp.getTime() > tenMinutesAgo;
  };

  // Format last updated time
  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  // Count Waze accidents
  const wazeCount = filteredAccidents.filter(a => a.source === 'waze').length;

  return (
    <div className="list-view">
      {/* Live Data Status Bar */}
      {userSettings.dataSources.waze && wazeDataState && (
        <div className={`live-status-bar ${wazeDataState.isLive ? 'live' : wazeDataState.error ? 'error' : 'offline'}`}>
          <div className="status-left">
            {wazeDataState.isLoading ? (
              <>
                <RefreshCw size={14} className="spinning" />
                <span>Updating...</span>
              </>
            ) : wazeDataState.isLive ? (
              <>
                <Wifi size={14} />
                <span className="live-badge">LIVE</span>
                <span className="waze-count">{wazeCount} Waze incidents</span>
              </>
            ) : wazeDataState.error ? (
              <>
                <AlertCircle size={14} />
                <span>Connection error</span>
              </>
            ) : (
              <>
                <WifiOff size={14} />
                <span>Offline</span>
              </>
            )}
          </div>
          <div className="status-right">
            {wazeDataState.lastUpdated && !wazeDataState.isLoading && (
              <span className="last-updated">
                Updated {formatLastUpdated(wazeDataState.lastUpdated)}
              </span>
            )}
            {onRefresh && (
              <button 
                className="refresh-btn"
                onClick={onRefresh}
                disabled={wazeDataState.isLoading}
                title="Refresh data"
              >
                <RefreshCw size={14} className={wazeDataState.isLoading ? 'spinning' : ''} />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="filter-controls">
        <div className="filter-toggle">
          <button
            className={`filter-btn ${filterBy === 'filtered' ? 'active' : ''}`}
            onClick={() => setFilterBy('filtered')}
          >
            Filtered
          </button>
          <button
            className={`filter-btn ${filterBy === 'all' ? 'active' : ''}`}
            onClick={() => setFilterBy('all')}
          >
            All
          </button>
        </div>
      </div>
      <div className="incidents-list">
        {wazeDataState?.isLoading && filteredAccidents.length === 0 ? (
          // Loading skeleton
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className="accident-skeleton">
                <div className="skeleton-line short"></div>
                <div className="skeleton-line medium"></div>
                <div className="skeleton-line full"></div>
                <div className="skeleton-line medium"></div>
              </div>
            ))}
          </>
        ) : filteredAccidents.length === 0 ? (
          // Empty state
          <div className="empty-state">
            <div className="empty-state-icon">
              <AlertCircle size={48} />
            </div>
            <h3>No incidents found</h3>
            <p>
              {wazeDataState?.error 
                ? 'Unable to load live data. Check your connection and try again.' 
                : 'There are currently no reported incidents in your zones.'}
            </p>
            {onRefresh && (
              <button 
                className="refresh-btn-large"
                onClick={onRefresh}
                disabled={wazeDataState?.isLoading}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            )}
          </div>
        ) : (
          filteredAccidents.map((accident) => (
            <AccidentCard
              key={accident.id}
              accident={accident}
              onViewOnMap={onViewOnMap}
              onUserClick={handleUserClick}
              onReplacementRequest={onReplacementRequest}
              isUserBlocked={userSettings.blockedUsers.includes(accident.submittedBy)}
              isTopItem={isRecentAccident(accident)}
            />
          ))
        )}
      </div>
      
      {selectedUser && (
        <UserBlockModal
          username={selectedUser}
          onBlock={handleBlockUser}
          onClose={handleCloseModal}
          isBlocked={userSettings.blockedUsers.includes(selectedUser)}
        />
      )}
    </div>
  );
}; 